import {RequiredLanguageProfile} from '../../../../../types/shared-types';
import {
  WORKFORCE_VERSION_EVOLUTION_COMPETENCY_SECTIONS,
  WORKFORCE_VERSION_EVOLUTION_KNOWLEDGE_SECTIONS,
  WorkforceProfileCompetencyRow,
  WorkforceProfileVersionEvolutionScoreDelta,
  WorkforceProfileVersionEvolutionScoreDeltaKey,
  WorkforceProfileVersionEvolutionSkillRef,
} from '../../types/WorkforceProfileTypes';


const normalizeSkillToken = (value: string | undefined): string => value?.trim().toLowerCase() ?? '';

export const getEvolutionSkillKey = (skill: RequiredLanguageProfile): string => {
  const en = normalizeSkillToken(skill.en);
  const de = normalizeSkillToken(skill.de);
  return `${en}|${de}`;
};

export const getEvolutionSkillKeyFromRow = (row: WorkforceProfileCompetencyRow): string => {
  const en = normalizeSkillToken(row.nameEn ?? row.name);
  const de = normalizeSkillToken(row.nameDe);
  return `${en}|${de}`;
};

export const rowMatchesEvolutionSkill = (
  row: WorkforceProfileCompetencyRow,
  skill: RequiredLanguageProfile
): boolean => {
  const rowKey = getEvolutionSkillKeyFromRow(row);
  const skillKey = getEvolutionSkillKey(skill);
  if (rowKey === skillKey) {
    return true;
  }

  const rowEn = normalizeSkillToken(row.nameEn ?? row.name);
  const rowDe = normalizeSkillToken(row.nameDe);
  const skillEn = normalizeSkillToken(skill.en);
  const skillDe = normalizeSkillToken(skill.de);

  return (!!rowEn && (rowEn === skillEn || rowEn === skillDe)) || (!!rowDe && (rowDe === skillEn || rowDe === skillDe));
};

export const buildNewlyMetKeySet = (
  entries: WorkforceProfileVersionEvolutionSkillRef[] | undefined,
  allowedSections: readonly string[]
): Set<string> => {
  const sectionSet = new Set(allowedSections);
  const keys = new Set<string>();

  (entries ?? []).forEach((entry) => {
    if (sectionSet.has(entry.section)) {
      keys.add(getEvolutionSkillKey(entry.skill));
    }
  });

  return keys;
};

export const COMPETENCY_EVOLUTION_SECTIONS = WORKFORCE_VERSION_EVOLUTION_COMPETENCY_SECTIONS;
export const KNOWLEDGE_EVOLUTION_SECTIONS = WORKFORCE_VERSION_EVOLUTION_KNOWLEDGE_SECTIONS;

const toDisplayPercent = (value: number | null | undefined): number | null => {
  if (value == null || Number.isNaN(value)) {
    return null;
  }

  if (value <= 1) {
    return Math.round(value * 100);
  }

  return Math.round(value);
};

const usesFractionScale = (key: WorkforceProfileVersionEvolutionScoreDeltaKey): boolean =>
  key === 'skillsCompetenciesAlignment' || key === 'knowledgeSkillsAlignment';

export const formatVersionEvolutionScoreValue = (
  key: WorkforceProfileVersionEvolutionScoreDeltaKey,
  value: number | null | undefined
): string | null => {
  const percent = usesFractionScale(key) ? toDisplayPercent(value) : toDisplayPercent(value);
  if (percent == null) {
    return null;
  }

  return `${percent}%`;
};

export const formatVersionEvolutionDelta = (
  key: WorkforceProfileVersionEvolutionScoreDeltaKey,
  delta: WorkforceProfileVersionEvolutionScoreDelta
): string | null => {
  if (delta.delta == null || Number.isNaN(delta.delta)) {
    return null;
  }

  const rounded = usesFractionScale(key) ? Math.round(delta.delta * 100) : Math.round(delta.delta);

  if (rounded === 0) {
    return null;
  }

  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded}%`;
};

export const getVersionEvolutionScoreDeltaEntries = (
  scoreDeltas: Record<string, WorkforceProfileVersionEvolutionScoreDelta | undefined>
): WorkforceProfileVersionEvolutionScoreDeltaKey[] =>
  (
    [
      'alignmentScore',
      'culturalFitScore',
      'skillsCompetenciesAlignment',
      'knowledgeSkillsAlignment',
      'responsibilitiesAlignment',
    ] as WorkforceProfileVersionEvolutionScoreDeltaKey[]
  ).filter((key) => scoreDeltas[key] != null);
